import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, Trash2, Download, Eye, Loader2, FileText, FileType2, FileOutput, Image as ImageIcon, Sheet, Search, X, ListChecks } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { aiApi, absUrl } from "./aiApi";
import SignToggle from "./SignToggle";

const kb = (n) => (n ? `${Math.max(1, Math.round(n / 1024))} KB` : "");
const iconOf = (m = "") => (m.startsWith("image/") ? ImageIcon : m.includes("wordprocessingml") || m === "application/msword" ? FileType2 : m.includes("sheet") ? Sheet : FileText);
const isWord = (m = "") => m.includes("wordprocessingml") || m === "application/msword";
const TYPES = [["", "Semua", "all"], ["word", "Word", "word"], ["pdf", "PDF", "pdf"], ["image", "Gambar", "image"], ["excel", "Excel", "excel"]];

export default function AIFilesPanel({ onOpenDoc, onPreview, cid, onCreated }) {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [ask, setAsk] = useState(null);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState([]);
  const [sign, setSign] = useState(false);
  const seq = useRef(0);
  const words = (rows || []).filter((f) => isWord(f.mime));
  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const stopSelect = () => { setSelecting(false); setPicked([]); };
  const load = (qq = q, tt = type) => {
    const n = ++seq.current;
    setRows(null);
    aiApi.myFiles(qq.trim(), tt).then((d) => { if (n === seq.current) { setRows(d.files || []); setCounts(d.counts || {}); } })
      .catch(() => n === seq.current && setRows([]));
  };
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => load(q, type), 300);
    return () => clearTimeout(t); // eslint-disable-next-line
  }, [q, type, open]);
  const del = async (f) => {
    setBusy(f.id);
    try { await aiApi.deleteFile(f.id); setRows((r) => r.filter((x) => x.id !== f.id)); toast.success(`'${f.filename}' dipadam`); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal padam"); } finally { setBusy(null); setAsk(null); }
  };
  const saveAsPdf = async (f) => {
    setBusy(`pdf-${f.id}`);
    try {
      const d = await aiApi.wordToPdf({ file_id: f.id, conversation_id: cid, sign });
      onCreated && onCreated(d);
      toast.success(`'${d.reply.doc_files[0].filename}' disimpan sebagai PDF`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan sebagai PDF"); } finally { setBusy(null); }
  };
  const bulkPdf = async () => {
    setBusy("bulk");
    try {
      const d = await aiApi.wordToPdfBulk({ file_ids: picked, conversation_id: cid, sign });
      onCreated && onCreated(d);
      if (d.failed?.length) toast.warning(`${d.converted} fail ditukar, ${d.failed.length} gagal: ${d.failed.map((x) => x.filename).join(", ")}`);
      else toast.success(`${d.converted} fail Word disimpan sebagai PDF`);
      stopSelect();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal tukar fail ke PDF"); } finally { setBusy(null); }
  };
  const openFile = (f) => {
    if (f.mime?.includes("wordprocessingml") || f.mime === "application/msword") onOpenDoc({ url: f.url, filename: f.filename });
    else if (f.mime?.startsWith("image/")) onPreview(absUrl(f.url));
    else window.open(absUrl(f.url), "_blank", "noopener");
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="h-8 px-3 rounded-full border border-slate-200 bg-white text-slate-600 text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-50 transition-colors" data-testid="ai-files-btn"><FolderOpen className="h-4 w-4" /> Fail</button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={8} className="w-80 p-0 z-[10001] flex flex-col max-h-[min(26rem,var(--radix-popover-content-available-height))]" data-testid="ai-files-panel">
        <div className="p-2 border-b border-slate-100 space-y-1.5">
          <div className="flex items-center justify-between gap-2 px-0.5">
            <div className="text-[11px] font-bold text-slate-500">Fail Mimi saya (disimpan di Atlas)</div>
            {words.length > 0 && (
              <button type="button" onClick={() => (selecting ? stopSelect() : setSelecting(true))} data-testid="ai-files-select-toggle"
                className={`shrink-0 h-6 px-2 rounded-full text-[10px] font-bold inline-flex items-center gap-1 border transition-colors ${selecting ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-rose-300"}`}>
                <ListChecks className="h-3 w-3" /> {selecting ? "Batal" : "PDF Pukal"}
              </button>
            )}
          </div>
          {selecting && (
            <div className="flex items-center justify-between text-[10px] text-slate-500 px-0.5" data-testid="ai-files-select-bar">
              <span>Tanda fail Word untuk ditukar ke PDF</span>
              <button type="button" className="font-bold text-rose-600" data-testid="ai-files-select-all"
                onClick={() => setPicked(picked.length === words.length ? [] : words.map((f) => f.id))}>
                {picked.length === words.length ? "Nyahpilih semua" : `Pilih semua (${words.length})`}
              </button>
            </div>
          )}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama fail..." data-testid="ai-files-search"
              className="w-full h-8 rounded-lg border border-slate-200 pl-7 pr-7 text-xs focus:outline-none focus:ring-2 focus:ring-fuchsia-200" />
            {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" aria-label="Kosongkan" data-testid="ai-files-search-clear"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <div className="flex gap-1 overflow-x-auto no-scrollbar" data-testid="ai-files-types">
            {TYPES.map(([v, l, k]) => (
              <button key={k} type="button" onClick={() => setType(v)} data-testid={`ai-files-type-${k}`}
                className={`shrink-0 h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-colors ${type === v ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-fuchsia-300"}`}>
                {l}{counts[k] != null ? ` (${counts[k]})` : ""}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-y-auto p-1 flex-1 min-h-0">
        {!rows ? <div className="h-16 grid place-items-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
          : rows.length === 0 ? <div className="text-xs text-slate-400 p-3" data-testid="ai-files-empty">{q || type ? "Tiada fail sepadan" : "Tiada fail lagi"}</div>
            : rows.map((f) => {
              const Icon = iconOf(f.mime);
              return (
                <div key={f.id} className="rounded-lg px-2 py-1.5 hover:bg-slate-50" data-testid="ai-file-item">
                  <div className="flex items-center gap-2">
                    {selecting && (isWord(f.mime)
                      ? <input type="checkbox" checked={picked.includes(f.id)} onChange={() => togglePick(f.id)} aria-label={`Pilih ${f.filename}`}
                          className="h-3.5 w-3.5 shrink-0 accent-rose-600" data-testid="ai-file-select-checkbox" />
                      : <span className="w-3.5 shrink-0" />)}
                    <Icon className="h-4 w-4 shrink-0 text-slate-400" />
                    <button className="flex-1 min-w-0 text-left" onClick={() => openFile(f)} data-testid="ai-file-open">
                      <span className="block text-xs truncate text-slate-700">{f.filename}</span>
                      <span className="block text-[10px] text-slate-400">{kb(f.size)} · {(f.created_at || "").slice(0, 10)}{f.version > 1 ? ` · v${f.version}` : ""}</span>
                    </button>
                    {isWord(f.mime) && (
                      <button onClick={() => saveAsPdf(f)} disabled={!!busy} title="Save As PDF terus (kekal format asal)" aria-label="Save As PDF" data-testid="ai-file-saveas-pdf"
                        className="h-6 px-1.5 rounded-md border border-rose-200 bg-rose-50 text-rose-600 text-[10px] font-bold inline-flex items-center gap-0.5 hover:bg-rose-100 transition-colors disabled:opacity-50">
                        {busy === `pdf-${f.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileOutput className="h-3 w-3" />} PDF
                      </button>
                    )}
                    <button onClick={() => openFile(f)} className="text-slate-400 hover:text-slate-700" aria-label="Buka"><Eye className="h-3.5 w-3.5" /></button>
                    <a href={`${absUrl(f.url)}&download=1`} className="text-slate-400 hover:text-slate-700" aria-label="Muat turun" data-testid="ai-file-download"><Download className="h-3.5 w-3.5" /></a>
                    <button onClick={() => setAsk(f.id)} className="text-rose-400 hover:text-rose-600" aria-label="Padam" data-testid="ai-file-delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  {ask === f.id && (
                    <div className="flex items-center gap-2 mt-1 rounded-lg bg-rose-50 px-2 py-1">
                      <span className="flex-1 text-[11px] font-semibold text-rose-700">Padam fail ini kekal?</span>
                      <button className="text-[11px] text-slate-500" onClick={() => setAsk(null)} data-testid="ai-file-delete-no">Tidak</button>
                      <button className="text-[11px] font-bold text-rose-600" onClick={() => del(f)} disabled={busy === f.id} data-testid="ai-file-delete-yes">{busy === f.id ? "..." : "Ya, padam"}</button>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        {selecting && (
          <div className="border-t border-slate-100 p-2 space-y-1.5 bg-slate-50/70" data-testid="ai-files-bulk-footer">
            <SignToggle checked={sign} onChange={setSign} testid="ai-files-sign-toggle" compact />
            <button type="button" onClick={bulkPdf} disabled={!picked.length || !!busy} data-testid="ai-files-bulk-pdf-btn"
              className="w-full h-8 rounded-lg bg-rose-600 text-white text-xs font-bold inline-flex items-center justify-center gap-1.5 hover:bg-rose-700 transition-colors disabled:opacity-50">
              {busy === "bulk" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileOutput className="h-3.5 w-3.5" />}
              {busy === "bulk" ? `Menukar ${picked.length} fail...` : `Tukar ${picked.length || ""} fail ke PDF`}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
